import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A simple countdown used for OTP lockouts (rate-limit "retry_after").
 * Call `start(seconds)` when the backend reports a lockout; `secondsLeft`
 * ticks down to 0 and `isLocked` flips to false when it elapses.
 */
export function useLockoutCountdown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds: number) => {
      clear();
      const initial = Math.max(0, Math.floor(seconds || 0));
      setSecondsLeft(initial);
      if (initial <= 0) return;
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clear]
  );

  useEffect(() => clear, [clear]);

  return { secondsLeft, isLocked: secondsLeft > 0, start };
}

/** Format a number of seconds as M:SS (e.g. 795 -> "13:15"). */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}
